import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: string[];
  permission?: string | string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles, permission }) => {
  const { user, loading } = useAuth();
  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];

  // Ainda carregando
  if (loading) {
    return null;
  }

  // Não autenticado
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Verificar permissões se roles foi especificado
  if (roles && roles.length > 0) {
    // Mapear roles antigas para perfis PT-BR para compatibilidade
    const roleMapping: Record<string, string> = {
      'admin': 'administrador',
      'financial': 'financeiro',
      'support': 'suporte',
      'client': 'cliente'
    };

    // Converter roles para perfis PT-BR se necessário
    const mappedRoles = roles.map(role => roleMapping[role] || role);
    
    const hasPermission = mappedRoles.includes(user.perfil);
    
    if (!hasPermission) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="background.default"
          p={3}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <Warning color="warning" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Acesso Negado
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Você não tem permissão para acessar esta página. Entre em contato com o administrador se precisar de acesso.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Seu perfil: <strong>{getPerfilLabel(user.perfil)}</strong>
            </Typography>
          </Paper>
        </Box>
      );
    }
  }

  if (permission) {
    const requiredPermissions = Array.isArray(permission) ? permission : [permission];
    const hasPermission = user?.perfil === 'administrador'
      || requiredPermissions.some((code) => userPermissions.includes(code));

    if (!hasPermission) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="background.default"
          p={3}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <Warning color="warning" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Acesso Negado
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Você não tem permissão para acessar esta página. Entre em contato com o administrador se precisar de acesso.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Seu perfil: <strong>{getPerfilLabel(user.perfil)}</strong>
            </Typography>
          </Paper>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

// Helper para converter perfil em label
const getPerfilLabel = (perfil: string): string => {
  const perfilLabels: { [key: string]: string } = {
    'administrador': 'Administrador',
    'financeiro': 'Financeiro',
    'suporte': 'Suporte',
    'cliente': 'Cliente',
  };
  
  return perfilLabels[perfil] || perfil;
};

export default ProtectedRoute; 